import { X, Upload, ChevronRight, ArrowLeft, CheckCircle2, Loader2, Circle, AlertTriangle, Search, XCircle, HelpCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/shared/components/ui/button";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import ProcRightLogo from "@/assets/icons/cakisan.svg";
import CelebrationLottie from "@/assets/Celebration.lottie";
import "./document-answer-panel.scss";
import { storageService } from "@/shared/services/api/storage-service";
import { documentAnswerProcessService } from "@/shared/services/api/document-answer-process-service";
import { ExploreSectionsEventEnum } from "@/shared/services/interface/responses/IExploreSupabase";
import type { DocumentAnswerProcessDetail, DocumentAnswerProcessListItem, DocumentAnswerProcessStatus } from "@/shared/services/interface/responses/document-answer-process";

type PanelView = "upload" | "progress" | "previous-analyses" | "completed";

interface UploadingFile {
  name: string;
  progress: number;
  totalBytes: number;
  status: "uploading" | "completed" | "error";
  fileId?: string;
}

interface ProgressStep {
  key: string;
  label: string;
  status: "done" | "active" | "pending";
  detail?: string;
}

interface CompletionResult {
  matchPercent: number;
  totalItems: number;
  answeredItems: number;
  yesCount: number;
  noCount: number;
  partiallyCount: number;
  conflictCount: number;
  conflictDetail: string;
  notFoundCount: number;
  notFoundDetail: string;
}

interface DocumentAnswerPanelProps {
  isOpen: boolean;
  onClose: () => void;
  totalItems: number;
  answeredItems: number;

  // Real flow props (DEV-1708)
  canUseRealFlow?: boolean;
  specificationId?: string;
  productId?: string;
  onProcessCompleted?: () => void;
}

// No mock/dummy previous analyses: only backend processes are shown.

const formatFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 KB";
  const KB = 1024;
  const MB = 1024 * 1024;

  if (bytes < MB) {
    // For small files (e.g. 115 KB), show KB so we don't end up displaying "1 MB".
    const kb = Math.max(1, Math.round(bytes / KB));
    return `${kb} KB`;
  }

  const mb = bytes / MB;
  // Keep it readable but accurate: 1.2MB -> "1.2 MB", 12MB -> "12 MB".
  const text = mb.toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
  return `${text} MB`;
};

export default function DocumentAnswerPanel({
  isOpen,
  onClose,
  totalItems,
  answeredItems,
  canUseRealFlow = false,
  specificationId,
  productId,
  onProcessCompleted,
}: DocumentAnswerPanelProps) {
  const { t } = useTranslation();
  const canUseBackendFlow = Boolean(canUseRealFlow && specificationId && productId);
  const [panelView, setPanelView] = useState<PanelView>("upload");
  const [uploadingFile, setUploadingFile] = useState<UploadingFile | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isPollingRef = useRef(false);

  // Progress view state
  const [progressSteps, setProgressSteps] = useState<ProgressStep[]>([]);
  const [progressText, setProgressText] = useState("");
  const [progressPercent, setProgressPercent] = useState(0);

  // Real flow (DEV-1708) state
  const [activeManuelProcessId, setActiveManuelProcessId] = useState<string | null>(null);
  const [processStatus, setProcessStatus] = useState<DocumentAnswerProcessStatus | null>(null);
  const [processDetail, setProcessDetail] = useState<DocumentAnswerProcessDetail | null>(null);
  const [previousProcesses, setPreviousProcesses] = useState<DocumentAnswerProcessListItem[]>([]);
  const [previousProcessDetailsById, setPreviousProcessDetailsById] = useState<
    Record<string, DocumentAnswerProcessDetail>
  >({});
  const [processError, setProcessError] = useState<string | null>(null);
  const [isStartingProcess, setIsStartingProcess] = useState(false);

  // Completion result state
  const [completionResult, setCompletionResult] = useState<CompletionResult | null>(null);

  // Cleanup timers
  useEffect(() => {
    return () => {
      if (uploadTimerRef.current) clearInterval(uploadTimerRef.current);
      if (pollingTimerRef.current) clearInterval(pollingTimerRef.current);
    };
  }, []);

  // Reset view when panel closes
  useEffect(() => {
    if (!isOpen) {
      // Small delay so animation finishes before reset
      const timer = setTimeout(() => {
        setPanelView("upload");
        setUploadingFile(null);
        setProgressSteps([]);
        setProgressText("");
        setProgressPercent(0);
        setCompletionResult(null);
        setActiveManuelProcessId(null);
        setProcessStatus(null);
        setProcessDetail(null);
        setPreviousProcesses([]);
        setPreviousProcessDetailsById({});
        setProcessError(null);
        setIsStartingProcess(false);
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const updateProgressForStatus = useCallback(
    (status: DocumentAnswerProcessStatus, opts?: { itemAnswersCount?: number }) => {
      setProcessStatus(status);

      // Base steps aligned with existing UI labels.
      const steps: ProgressStep[] = [
        { key: "upload", label: t("doc-uploaded"), status: "done", detail: "00:00" },
        { key: "install", label: t("ocr-completed"), status: "pending" },
        {
          key: "matching",
          label: t("item-matching"),
          status: "pending",
          detail: t("items-processed", { done: 0, total: totalItems }),
        },
        { key: "results", label: t("results-preparing"), status: "pending" },
      ];

      const setStep = (key: string, next: Partial<ProgressStep>) => {
        const i = steps.findIndex((s) => s.key === key);
        if (i >= 0) steps[i] = { ...steps[i], ...next };
      };

      if (status === "QUEUED") {
        setStep("install", { status: "active" });
        setProgressPercent(10);
        setProgressText(t("ai-analyzing"));
      } else if (status === "DOCUMENT_INSTALLED") {
        setStep("install", { status: "done" });
        setStep("matching", { status: "active" });
        setProgressPercent(35);
        setProgressText(t("ai-analyzing"));
      } else if (status === "DOCUMENT_PROCESSED") {
        setStep("install", { status: "done" });
        setStep("matching", {
          status: "active",
          detail: t("items-processed", {
            done: opts?.itemAnswersCount ?? 0,
            total: totalItems,
          }),
        });
        setProgressPercent(70);
        setProgressText(t("ai-analyzing"));
      } else if (status === "COMPLETED") {
        setStep("install", { status: "done" });
        setStep("matching", {
          status: "done",
          detail: t("items-processed", {
            done: opts?.itemAnswersCount ?? totalItems,
            total: totalItems,
          }),
        });
        setStep("results", { status: "done" });
        setProgressPercent(100);
        setProgressText("");
      } else if (status === "DOCUMENT_NOT_RELEVANT") {
        setProgressPercent(100);
        setProgressText("");
        setProcessError(t("document-not-relevant"));
      } else if (status === "ERROR") {
        setProgressPercent(100);
        setProgressText("");
        setProcessError(t("error:networkError"));
      }

      setProgressSteps(steps);
    },
    [t, totalItems]
  );

  const showCompletedFromDetail = useCallback(
    (detail: DocumentAnswerProcessDetail) => {
      const matchPercent = detail.matchPercent ?? 0;
      const cTotal = detail.totalItems ?? totalItems;
      const cAnswered = detail.answeredItems ?? answeredItems;

      setCompletionResult({
        matchPercent,
        totalItems: cTotal,
        answeredItems: cAnswered,
        yesCount: detail.yesCount ?? 0,
        noCount: detail.noCount ?? 0,
        partiallyCount: detail.partiallyCount ?? 0,
        conflictCount: detail.conflictCount ?? 0,
        conflictDetail: t("conflict-detail", { count: detail.conflictCount ?? 0 }),
        notFoundCount: detail.notFoundCount ?? 0,
        notFoundDetail: t("not-found-detail", { count: detail.notFoundCount ?? 0 }),
      });
      setPanelView("completed");
    },
    [answeredItems, t, totalItems]
  );

  // Fetch previous processes on open (real flow)
  useEffect(() => {
    if (!isOpen || !canUseBackendFlow || !specificationId) return;
    documentAnswerProcessService
      .listProcesses({ specificationId })
      .then((res) => {
        if (res.success) setPreviousProcesses(res.data ?? []);
      })
      .catch(() => {
        // Non-blocking (panel still works without list)
      });
  }, [isOpen, canUseBackendFlow, specificationId]);

  // Fetch details for previous processes so we can render "results cards" (Figma)
  useEffect(() => {
    if (!isOpen || !canUseBackendFlow || !specificationId) return;
    if (previousProcesses.length === 0) return;

    let cancelled = false;

    const missingIds = previousProcesses
      .map((p) => p.manuelProcessId)
      .filter((id) => !previousProcessDetailsById[id]);

    if (missingIds.length === 0) return;

    Promise.allSettled(
      missingIds.slice(0, 10).map(async (manuelProcessId) => {
        const res = await documentAnswerProcessService.getProcessDetail({
          specificationId,
          manuelProcessId,
        });
        if (!res.success || !res.data) return null;
        return { manuelProcessId, detail: res.data };
      })
    ).then((results) => {
      if (cancelled) return;
      setPreviousProcessDetailsById((prev) => {
        const next = { ...prev };
        for (const r of results) {
          if (r.status !== "fulfilled" || !r.value) continue;
          next[r.value.manuelProcessId] = r.value.detail;
        }
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [
    canUseBackendFlow,
    isOpen,
    previousProcessDetailsById,
    previousProcesses,
    specificationId,
  ]);

  // Realtime: subscribe to explore events and update progress (real flow)
  useEffect(() => {
    if (!isOpen || !canUseBackendFlow || !specificationId || !activeManuelProcessId) return;

    let cancelled = false;
    let subscription: { unsubscribe?: () => void } | null = null;

    (async () => {
      try {
        const mod = await import("@/shared/services/supabase/subscription-adapter");
        if (cancelled) return;

        subscription = mod.optimizedSupabaseCompareService.listenExploreChanges(
          (response: any) => {
            const event = response?.event as ExploreSectionsEventEnum | string | undefined;
            const payload = response?.payload ?? response?.new ?? response?.data ?? {};

            // Status updates
            if (event === ExploreSectionsEventEnum.DocumentAnswerStatus) {
              if (payload?.manuelProcessId && payload.manuelProcessId !== activeManuelProcessId) return;
              const nextStatus = payload?.status as DocumentAnswerProcessStatus | undefined;
              if (nextStatus) {
                updateProgressForStatus(nextStatus, { itemAnswersCount: payload?.itemAnswersCount });
                if (nextStatus === "DOCUMENT_NOT_RELEVANT") {
                  setProcessError(payload?.message ?? t("document-not-relevant"));
                }
                if (nextStatus === "ERROR") {
                  setProcessError(payload?.message ?? t("error:networkError"));
                }
              }
            }

            // Item-level progress
            if (event === ExploreSectionsEventEnum.DocumentAnswerItemProgress) {
              if (payload?.manuelProcessId && payload.manuelProcessId !== activeManuelProcessId) return;
              const itemAnswersCount = payload?.itemAnswersCount as number | undefined;
              if (typeof itemAnswersCount === "number") {
                updateProgressForStatus(processStatus ?? "DOCUMENT_PROCESSED", { itemAnswersCount });
              }
            }
          },
          specificationId
        );
      } catch {
        // Non-blocking: polling fallback still runs.
      }
    })();

    return () => {
      cancelled = true;
      subscription?.unsubscribe?.();
    };
  }, [
    activeManuelProcessId,
    canUseBackendFlow,
    isOpen,
    processStatus,
    specificationId,
    t,
    updateProgressForStatus,
  ]);

  // Polling fallback (real flow)
  useEffect(() => {
    if (!isOpen || !canUseBackendFlow || !specificationId || !activeManuelProcessId) return;
    if (panelView !== "progress") return;

    if (pollingTimerRef.current) clearInterval(pollingTimerRef.current);

    pollingTimerRef.current = setInterval(async () => {
      if (isPollingRef.current) return;
      isPollingRef.current = true;
      try {
        const res = await documentAnswerProcessService.getProcessDetail({
          specificationId,
          manuelProcessId: activeManuelProcessId,
        });
        if (res.success && res.data) {
          setProcessDetail(res.data);
          if (res.data.status) updateProgressForStatus(res.data.status);

          if (res.data.status === "COMPLETED") {
            showCompletedFromDetail(res.data);
            onProcessCompleted?.();
            if (pollingTimerRef.current) clearInterval(pollingTimerRef.current);
          }
          if (res.data.status === "DOCUMENT_NOT_RELEVANT") {
            setProcessError(res.data.errorMessage ?? t("document-not-relevant"));
            if (pollingTimerRef.current) clearInterval(pollingTimerRef.current);
          }
          if (res.data.status === "ERROR") {
            setProcessError(res.data.errorMessage ?? t("error:networkError"));
            if (pollingTimerRef.current) clearInterval(pollingTimerRef.current);
          }
        }
      } catch {
        // ignore: realtime may still update, polling is best-effort
      } finally {
        isPollingRef.current = false;
      }
    }, 3000);

    return () => {
      if (pollingTimerRef.current) clearInterval(pollingTimerRef.current);
    };
  }, [
    activeManuelProcessId,
    canUseBackendFlow,
    isOpen,
    onProcessCompleted,
    panelView,
    showCompletedFromDetail,
    specificationId,
    t,
    updateProgressForStatus,
  ]);

  const handleRetryProcess = useCallback(() => {
    setProcessError(null);
    setProcessDetail(null);
    setProcessStatus(null);
    setActiveManuelProcessId(null);
    setProgressSteps([]);
    setProgressText("");
    setProgressPercent(0);
    setPanelView("upload");
  }, []);

  const handleOpenPreviousProcess = useCallback(
    async (manuelProcessId: string) => {
      if (!canUseBackendFlow || !specificationId) return;
      setProcessError(null);
      setActiveManuelProcessId(manuelProcessId);
      setPanelView("progress");
      try {
        const res = await documentAnswerProcessService.getProcessDetail({
          specificationId,
          manuelProcessId,
        });
        if (res.success && res.data) {
          setProcessDetail(res.data);
          if (res.data.status) updateProgressForStatus(res.data.status);
          if (res.data.status === "COMPLETED") {
            showCompletedFromDetail(res.data);
          }
          if (res.data.status === "DOCUMENT_NOT_RELEVANT") {
            setProcessError(res.data.errorMessage ?? t("document-not-relevant"));
          }
          if (res.data.status === "ERROR") {
            setProcessError(res.data.errorMessage ?? t("error:networkError"));
          }
        }
      } catch {
        setProcessError(t("error:networkError"));
      }
    },
    [canUseBackendFlow, showCompletedFromDetail, specificationId, t, updateProgressForStatus]
  );

  const simulateUpload = useCallback((fileName: string, totalBytes: number) => {
    setUploadingFile({
      name: fileName,
      progress: 0,
      totalBytes,
      status: "uploading",
    });

    let progress = 0;
    uploadTimerRef.current = setInterval(() => {
      progress += Math.random() * 15 + 5;
      if (progress >= 100) {
        progress = 100;
        if (uploadTimerRef.current) clearInterval(uploadTimerRef.current);
        setUploadingFile((prev) =>
          prev ? { ...prev, progress: 100, status: "completed" } : null
        );
      } else {
        setUploadingFile((prev) =>
          prev
            ? { ...prev, progress: Math.round(progress) }
            : null
        );
      }
    }, 300);
  }, []);

  const handleFileSelect = useCallback(
    async (file: File) => {
      const totalBytes = file.size;

      // Default (tests / legacy): simulated upload
      if (!canUseBackendFlow) {
        simulateUpload(file.name, totalBytes);
        return;
      }

      setProcessError(null);
      setUploadingFile({
        name: file.name,
        progress: 0,
        totalBytes,
        status: "uploading",
      });

      try {
        const form = new FormData();
        // Backend upload endpoint expects `files` (array-like).
        form.append("files", file);

        const response = await storageService.uploadFile(form, (progress) => {
          setUploadingFile((prev) =>
            prev
              ? {
                  ...prev,
                  progress,
                }
              : null
          );
        });

        const fileId = response.data?.[0]?.id;
        if (!response.success || !fileId) {
          throw new Error("upload failed: missing fileId");
        }

        setUploadingFile((prev) =>
          prev
            ? {
                ...prev,
                progress: 100,
                status: "completed",
                fileId,
              }
            : null
        );
      } catch (e) {
        setUploadingFile((prev) => (prev ? { ...prev, status: "error" } : null));
        setProcessError(t("error:networkError"));
      }
    },
    [canUseBackendFlow, simulateUpload, t]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleClickUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFileSelect(file);
      // Reset so same file can be selected again
      e.target.value = "";
    },
    [handleFileSelect]
  );

  const cancelUpload = useCallback(() => {
    if (uploadTimerRef.current) clearInterval(uploadTimerRef.current);
    setUploadingFile(null);
  }, []);

  // Devam Et
  const handleContinue = useCallback(async () => {
    setProcessError(null);
    setPanelView("progress");

    // Real flow (DEV-1708)
    if (canUseBackendFlow) {
      const fileId = uploadingFile?.fileId;
      if (!specificationId || !productId || !fileId) {
        setProcessError(t("error:badRequest"));
        return;
      }

      setIsStartingProcess(true);
      try {
        // Initial UI state
        updateProgressForStatus("QUEUED");

        const startRes = await documentAnswerProcessService.startProcess({
          specificationId,
          productId,
          fileId,
        });

        if (!startRes.success || !startRes.data?.manuelProcessId) {
          throw new Error("process start failed");
        }

        const manuelProcessId = startRes.data.manuelProcessId;
        setActiveManuelProcessId(manuelProcessId);
        updateProgressForStatus(startRes.data.status ?? "QUEUED");

        const detailRes = await documentAnswerProcessService.getProcessDetail({
          specificationId,
          manuelProcessId,
        });
        if (detailRes.success) {
          setProcessDetail(detailRes.data);
          if (detailRes.data?.status) {
            updateProgressForStatus(detailRes.data.status);
          }
        }
      } catch (e) {
        setProcessError(t("error:networkError"));
      } finally {
        setIsStartingProcess(false);
      }

      return;
    }

    // Legacy: start AI progress simulation (used by tests)
    const steps: ProgressStep[] = [
      { key: "upload", label: t("doc-uploaded"), status: "done", detail: "00:00" },
      { key: "ocr", label: t("ocr-completed"), status: "active", detail: "00:12" },
      {
        key: "matching",
        label: t("item-matching"),
        status: "pending",
        detail: t("items-processed", { done: 0, total: totalItems }),
      },
      { key: "results", label: t("results-preparing"), status: "pending" },
    ];
    setProgressSteps(steps);
    setProgressText(t("scanning-page", { current: 1, total: 23 }));
    setProgressPercent(10);

    let step = 1;
    const interval = setInterval(() => {
      step++;
      if (step === 2) {
        setProgressSteps((prev) =>
          prev.map((s) =>
            s.key === "ocr"
              ? { ...s, status: "done" }
              : s.key === "matching"
                ? {
                    ...s,
                    status: "active",
                    detail: t("items-processed", { done: 350, total: 400 }),
                  }
                : s
          )
        );
        setProgressText(t("scanning-page", { current: 15, total: 23 }));
        setProgressPercent(60);
      } else if (step === 3) {
        setProgressSteps((prev) =>
          prev.map((s) =>
            s.key === "matching"
              ? {
                  ...s,
                  status: "done",
                  detail: t("items-processed", { done: 400, total: 400 }),
                }
              : s.key === "results"
                ? { ...s, status: "active" }
                : s
          )
        );
        setProgressText(t("scanning-page", { current: 23, total: 23 }));
        setProgressPercent(90);
      } else {
        clearInterval(interval);
        setProgressSteps((prev) => prev.map((s) => ({ ...s, status: "done" as const })));
        setProgressPercent(100);
        setProgressText("");
        setTimeout(() => {
          setCompletionResult({
            matchPercent: 87,
            totalItems: 400,
            answeredItems: 200,
            yesCount: 320,
            noCount: 25,
            partiallyCount: 5,
            conflictCount: 3,
            conflictDetail: t("conflict-detail", { count: 2 }),
            notFoundCount: 30,
            notFoundDetail: t("not-found-detail", { count: 30 }),
          });
          setPanelView("completed");
        }, 1000);
      }
    }, 2000);

    uploadTimerRef.current = interval;
  }, [
    canUseBackendFlow,
    productId,
    specificationId,
    t,
    totalItems,
    updateProgressForStatus,
    uploadingFile?.fileId,
  ]);

  const handleCancelProgress = useCallback(() => {
    if (uploadTimerRef.current) clearInterval(uploadTimerRef.current);
    if (pollingTimerRef.current) clearInterval(pollingTimerRef.current);
    setPanelView("upload");
    setUploadingFile(null);
    setActiveManuelProcessId(null);
    setProcessStatus(null);
    setProcessDetail(null);
    setProcessError(null);
  }, []);

  // --- RENDER ---

  const renderHeader = () => {
    if (panelView === "previous-analyses") {
      return (
        <div className="document-answer-panel__header">
          <div className="flex items-center gap-2">
            <button onClick={() => setPanelView("upload")} className="document-answer-panel__close-btn">
              <ArrowLeft width={16} height={16} />
            </button>
            <div className="document-answer-panel__header-text">
              <h3 className="text-sm font-600 text-[#09090B]">{t("previous-analyses")}</h3>
              <p className="text-xs text-[#71717A] font-400">{t("previous-analyses-subtitle")}</p>
            </div>
          </div>
          <button onClick={onClose} className="document-answer-panel__close-btn">
            <X width={16} height={16} />
          </button>
        </div>
      );
    }

    return (
      <div className="document-answer-panel__header">
        <div className="document-answer-panel__header-text">
          <h3 className="text-sm font-600 text-[#09090B]">{t("answer-with-document")}</h3>
          <p className="text-xs text-[#71717A] font-400">{t("doc-panel-subtitle")}</p>
        </div>
        <button onClick={onClose} className="document-answer-panel__close-btn">
          <X width={16} height={16} />
        </button>
      </div>
    );
  };

  const renderUploadView = () => (
    <>
      <div className="document-answer-panel__content">
        {/* Mevcut Durum */}
        <div className="document-answer-panel__section">
          <span className="text-xs font-600 text-[#09090B]">{t("current-status")}</span>
          <div className="document-answer-panel__status-cards">
            <div className="document-answer-panel__status-card">
              <span className="text-[11px] text-[#71717A] font-400">{t("total-items-label")}</span>
              <span className="text-lg font-700 text-[#09090B]">{totalItems}</span>
            </div>
            <div className="document-answer-panel__status-card">
              <span className="text-[11px] text-[#71717A] font-400">{t("answered-label")}</span>
              <span className="text-lg font-700 text-[#09090B]">{answeredItems}</span>
            </div>
          </div>
        </div>

        {/* File Drop Zone */}
        <div
          className={`document-answer-panel__dropzone ${isDragOver ? "drag-over" : ""}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleClickUpload}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".docx,.pdf"
            className="hidden"
            onChange={handleInputChange}
          />
          <Upload width={20} height={20} className="text-[#71717A]" />
          <p className="text-xs text-[#09090B] mt-2">
            <span className="underline font-500 cursor-pointer">
              {t("doc-click-to-upload")}
            </span>{" "}
            <span className="font-400 text-[#71717A]">{t("or-drag-and-drop")}</span>
          </p>
          <p className="text-[11px] text-[#A1A1AA] font-400 mt-0.5">{t("file-restrictions")}</p>
        </div>

        {/* Son Yüklenenler */}
        {uploadingFile && (
          <div className="document-answer-panel__section">
            <span className="text-xs font-600 text-[#09090B]">{t("recent-uploads")}</span>
            <div className="document-answer-panel__file-item">
              <div className="document-answer-panel__file-icon">
                <Upload width={14} height={14} className="text-[#71717A]" />
              </div>
              <div className="document-answer-panel__file-info">
                <span className="text-xs font-500 text-[#09090B] truncate">{uploadingFile.name}</span>
                <span className="text-[11px] text-[#A1A1AA] font-400">
                  {formatFileSize(
                    Math.round((uploadingFile.progress / 100) * uploadingFile.totalBytes)
                  )}{" "}
                  of {formatFileSize(uploadingFile.totalBytes)} ·{" "}
                  {uploadingFile.status === "completed" ? (
                    <span className="text-green-600">● Completed</span>
                  ) : uploadingFile.status === "error" ? (
                    <span className="text-red-600">● {t("error:networkError")}</span>
                  ) : (
                    <span>⏳ {t("uploading")}</span>
                  )}
                </span>
                {/* Progress bar */}
                <div className="document-answer-panel__file-progress">
                  <div
                    className="document-answer-panel__file-progress-bar"
                    style={{ width: `${uploadingFile.progress}%` }}
                  />
                </div>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[11px] text-[#A1A1AA]">%{uploadingFile.progress}</span>
                <button onClick={cancelUpload} className="document-answer-panel__close-btn" style={{ width: 20, height: 20 }}>
                  <X width={12} height={12} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Önceki Analizler */}
        {canUseBackendFlow && previousProcesses.length > 0 && (
          <div className="document-answer-panel__section">
            <span className="text-xs font-600 text-[#09090B]">{t("previous-analyses")}</span>
            {previousProcesses.map((analysis: any) => (
              <div
                key={analysis.manuelProcessId}
                className="document-answer-panel__analysis-item"
                onClick={() => {
                  setPanelView("previous-analyses");
                }}
              >
                <div className="document-answer-panel__file-icon">
                  <Upload width={14} height={14} className="text-[#71717A]" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-500 text-[#09090B] truncate block">
                    {analysis.fileName ?? analysis.manuelProcessId}
                  </span>
                  <span className="text-[11px] text-[#A1A1AA] font-400">
                    {analysis.createdAt ?? ""}
                  </span>
                </div>
                <ChevronRight width={14} height={14} className="text-[#A1A1AA] flex-shrink-0" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Devam Et button - content dışında, panel altına sticky */}
      <div className="document-answer-panel__footer">
        <Button
          className="w-full h-9 text-sm font-600"
          disabled={
            !uploadingFile ||
            uploadingFile.status !== "completed" ||
            (canUseBackendFlow && !uploadingFile.fileId) ||
            isStartingProcess
          }
          onClick={() => void handleContinue()}
          isLoading={isStartingProcess}
        >
          {t("continue-btn")}
        </Button>
      </div>
    </>
  );

  const renderProgressView = () => (
    <>
      <div className="document-answer-panel__content">
        {/* Uploaded file info */}
        {uploadingFile && (
          <div className="document-answer-panel__section">
            <span className="text-xs font-600 text-[#09090B]">{t("uploaded-file")}</span>
            <div className="document-answer-panel__file-item" style={{ border: "none", padding: "4px 0" }}>
              <div className="document-answer-panel__file-icon">
                <Upload width={14} height={14} className="text-[#71717A]" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-xs font-500 text-[#09090B] truncate block">{uploadingFile.name}</span>
                <span className="text-[11px] text-[#A1A1AA]">
                  {formatFileSize(uploadingFile.totalBytes)} · {230} {t("page-count")}
                </span>
              </div>
            </div>
          </div>
        )}

        {processError && (
          <div className="document-answer-panel__section">
            <div className="flex items-start gap-2 rounded-[8px] border border-[#FECACA] bg-[#FEF2F2] p-3">
              <AlertTriangle width={16} height={16} className="text-red-600 mt-0.5" />
              <div className="flex-1">
                <div className="text-xs font-600 text-[#09090B]">{t("error")}</div>
                <div className="text-[11px] text-[#71717A]">{processError}</div>
              </div>
              <button
                type="button"
                className="text-[11px] font-600 text-[#09090B] underline"
                onClick={handleRetryProcess}
              >
                {t("retry")}
              </button>
            </div>
          </div>
        )}

        {/* AI Analysis animation */}
        <div className="document-answer-panel__ai-area">
          <img src={ProcRightLogo} alt="AI" width={36} height={36} className="mx-auto" />
          <p className="text-sm font-600 text-[#09090B] text-center mt-2">{t("ai-analyzing")}</p>
          {progressText && (
            <p className="text-xs text-[#71717A] text-center mt-0.5">
              {progressText} · %{progressPercent}
            </p>
          )}
          {/* Mini progress bar */}
          <div className="document-answer-panel__ai-progress">
            <div
              className="document-answer-panel__ai-progress-bar"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* Steps */}
        <div className="document-answer-panel__steps">
          {progressSteps.map((step) => (
            <div key={step.key} className="document-answer-panel__step">
              <div className="document-answer-panel__step-icon">
                {step.status === "done" ? (
                  <CheckCircle2 width={16} height={16} className="text-green-600" />
                ) : step.status === "active" ? (
                  <Loader2 width={16} height={16} className="text-[#09090B] animate-spin" />
                ) : (
                  <Circle width={16} height={16} className="text-[#D4D4D8]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <span
                  className={`text-xs font-500 block ${
                    step.status === "pending" ? "text-[#D4D4D8]" : "text-[#09090B]"
                  }`}
                >
                  {step.label}
                </span>
                {step.detail && (
                  <span className="text-[11px] text-[#A1A1AA] font-400">{step.detail}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Average time */}
        <p className="text-xs text-[#A1A1AA] font-400 text-center mt-4">{t("average-time")}</p>
      </div>

      {/* Cancel button - content dışında, panel altına sticky */}
      <div className="document-answer-panel__footer">
        <Button variant="outline" className="w-full h-9 text-sm font-500" onClick={handleCancelProgress}>
          {t("cancel-btn")}
        </Button>
      </div>
    </>
  );

  const renderPreviousAnalysesView = () => {
    if (!canUseBackendFlow) return null;
    const items = previousProcesses;

    return (
      <>
        <div className="document-answer-panel__content">
          {items.map((item: any) => {
            const id = item.manuelProcessId as string;
            const detail = previousProcessDetailsById[id];

            const fileName = item.fileName ?? id;
            const date = item.createdAt ?? "";
            const pageCount = null;

            const matchPercent = detail?.matchPercent;
            const answered = detail?.answeredItems;
            const total = detail?.totalItems;
            const yes = detail?.yesCount;
            const no = detail?.noCount;
            const partial = detail?.partiallyCount;
            const notFound = detail?.notFoundCount;
            const conflicts = detail?.conflictCount;

            return (
              <div
                key={id}
                className="document-answer-panel__analysis-card"
                role="button"
                tabIndex={0}
                onClick={() => {
                  void handleOpenPreviousProcess(id);
                }}
              >
                <div className="document-answer-panel__analysis-card-header">
                  <div className="document-answer-panel__file-icon">
                    <Upload width={14} height={14} className="text-[#71717A]" />
                  </div>

                  <div className="document-answer-panel__analysis-card-title">
                    <div className="text-xs font-500 text-[#09090B] truncate">{fileName}</div>
                    <div className="text-[11px] text-[#A1A1AA]">
                      {date}
                      {pageCount !== null ? (
                        <>
                          {" "}
                          · {pageCount} {t("page-count")}
                        </>
                      ) : null}
                    </div>
                  </div>

                  {typeof matchPercent === "number" ? (
                    <div className="document-answer-panel__analysis-card-percent">{matchPercent}%</div>
                  ) : (
                    <div className="document-answer-panel__analysis-card-percent document-answer-panel__analysis-card-percent--muted">
                      {t("loading")}
                    </div>
                  )}
                </div>

                {/* Percent bar (Figma) */}
                <div className="document-answer-panel__analysis-card-bar">
                  <div
                    className="document-answer-panel__analysis-card-bar-fill"
                    style={{ width: `${Math.max(0, Math.min(100, matchPercent ?? 0))}%` }}
                  />
                </div>

                <div className="document-answer-panel__analysis-card-summary">
                  <span className="text-[11px] text-[#09090B] font-500">
                    {typeof answered === "number" && typeof total === "number"
                      ? `${answered}/${total}`
                      : "-/-"}{" "}
                    {t("items-matched")}
                  </span>

                  <div className="document-answer-panel__analysis-card-counts">
                    <span className="text-[11px] font-400">
                      <span className="text-green-600">●</span> {t("yes")}: {yes ?? "-"}
                    </span>
                    <span className="text-[11px] font-400">
                      <span className="text-red-500">●</span> {t("no")}: {no ?? "-"}
                    </span>
                    <span className="text-[11px] font-400">
                      <span className="text-yellow-500">●</span> {t("partially")}: {partial ?? "-"}
                    </span>
                  </div>
                </div>

                <div className="document-answer-panel__analysis-card-notes">
                  {typeof notFound === "number" ? (
                    <div className="text-[11px] text-[#71717A]">
                      {t("not-found-detail", { count: notFound })}
                    </div>
                  ) : null}
                  {typeof conflicts === "number" && conflicts > 0 ? (
                    <div className="text-[11px] text-[#71717A]">
                      {t("conflict-detail", { count: conflicts })}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        <div className="document-answer-panel__footer">
          <button
            className="document-answer-panel__upload-btn"
            onClick={() => {
              setUploadingFile(null);
              setPanelView("upload");
            }}
          >
            <Upload width={18} height={18} />
            {t("new-document")}
          </button>
        </div>
      </>
    );
  };

  // Gauge animation state
  const [animatedPercent, setAnimatedPercent] = useState(0);
  const [gaugeAnimationStarted, setGaugeAnimationStarted] = useState(false);
  const gaugeArcRef = useRef<SVGPathElement>(null);

  // Trigger gauge animation when completed view appears
  useEffect(() => {
    if (panelView === "completed" && completionResult && !gaugeAnimationStarted) {
      setGaugeAnimationStarted(true);
      setAnimatedPercent(0);

      const targetPercent = completionResult.matchPercent;
      const duration = 1500; // 1.5 seconds
      const startTime = performance.now();

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease-out cubic for smooth deceleration
        const eased = 1 - Math.pow(1 - progress, 3);
        const currentPercent = Math.round(eased * targetPercent);

        setAnimatedPercent(currentPercent);

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      // Small delay to ensure DOM is ready, then start animation
      requestAnimationFrame(() => {
        requestAnimationFrame(animate);
      });
    }
  }, [panelView, completionResult, gaugeAnimationStarted]);

  // Reset gauge animation state when panel closes or view changes away from completed
  useEffect(() => {
    if (panelView !== "completed") {
      setGaugeAnimationStarted(false);
      setAnimatedPercent(0);
    }
  }, [panelView]);

  const renderGauge = (percent: number) => {
    // Arc path matching HTML reference: M 25 120 A 85 85 0 0 1 195 120
    const arcPath = "M 25 120 A 85 85 0 0 1 195 120";
    const totalArcLength = 267;
    const displayPercent = gaugeAnimationStarted ? animatedPercent : 0;
    const arcOffset = totalArcLength - (totalArcLength * displayPercent) / 100;

    return (
      <div className="document-answer-panel__gauge">
        {/* Celebration Lottie animation */}
        <div className="document-answer-panel__celebration-lottie">
          <DotLottieReact
            src={CelebrationLottie}
            autoplay
            loop={false}
            style={{ width: "100%", height: "100%" }}
          />
        </div>
        <div className="document-answer-panel__gauge-wrapper">
          <svg className="document-answer-panel__gauge-svg" viewBox="0 0 220 130">
            <defs>
              <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#66BB6A" />
                <stop offset="50%" stopColor="#4CAF50" />
                <stop offset="100%" stopColor="#388E3C" />
              </linearGradient>
            </defs>
            {/* Background arc */}
            <path d={arcPath} fill="none" stroke="#EEEEEE" strokeWidth={18} strokeLinecap="round" />
            {/* Filled arc - animated */}
            <path
              ref={gaugeArcRef}
              d={arcPath}
              fill="none"
              stroke="url(#gaugeGradient)"
              strokeWidth={18}
              strokeLinecap="round"
              strokeDasharray={totalArcLength}
              strokeDashoffset={arcOffset}
            />
          </svg>
          <div className="document-answer-panel__gauge-percentage">
            <span>{displayPercent}</span>%
          </div>
        </div>
        <div className="document-answer-panel__gauge-labels">
          <span>0</span>
          <span>100</span>
        </div>
      </div>
    );
  };

  const renderCompletedView = () => {
    if (!completionResult) return null;
    const { matchPercent, totalItems: cTotal, answeredItems: cAnswered, yesCount, noCount, partiallyCount, conflictCount, conflictDetail, notFoundCount, notFoundDetail } = completionResult;

    return (
      <>
        <div className="document-answer-panel__completed-body">
          {/* Gauge Section */}
          <div className="document-answer-panel__gauge-section">
            {renderGauge(matchPercent)}

            <h3 className="document-answer-panel__result-title">{t("analysis-completed")}</h3>
            <p className="document-answer-panel__result-subtitle">
              <strong>{cTotal}/{cAnswered}</strong> {t("items-auto-answered")}
            </p>

            {/* Stats Row */}
            <div className="document-answer-panel__stats-row">
              <div className="document-answer-panel__stat-item">
                <div className="document-answer-panel__stat-icon document-answer-panel__stat-icon--yes">
                  <CheckCircle2 width={12} height={12} />
                </div>
                <span className="document-answer-panel__stat-label">{t("yes")}:</span>
                <span className="document-answer-panel__stat-value document-answer-panel__stat-value--green">{yesCount}</span>
              </div>
              <div className="document-answer-panel__stat-item">
                <div className="document-answer-panel__stat-icon document-answer-panel__stat-icon--no">
                  <XCircle width={12} height={12} />
                </div>
                <span className="document-answer-panel__stat-label">{t("no")}:</span>
                <span className="document-answer-panel__stat-value document-answer-panel__stat-value--red">{noCount}</span>
              </div>
              <div className="document-answer-panel__stat-item">
                <div className="document-answer-panel__stat-icon document-answer-panel__stat-icon--partial">
                  <HelpCircle width={12} height={12} />
                </div>
                <span className="document-answer-panel__stat-label">{t("partially")}:</span>
                <span className="document-answer-panel__stat-value document-answer-panel__stat-value--orange">{partiallyCount}</span>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="document-answer-panel__divider" />

          {/* Resolve Section */}
          <div className="document-answer-panel__resolve-section">
            <h3 className="document-answer-panel__resolve-title">{t("resolve-answers")}</h3>

            {/* Çakışan Cevaplar - active state */}
            <div className="document-answer-panel__resolve-card document-answer-panel__resolve-card--active">
              <div className="document-answer-panel__resolve-icon document-answer-panel__resolve-icon--conflict">
                <AlertTriangle width={20} height={20} />
              </div>
              <div className="document-answer-panel__resolve-card-text">
                <h4>{t("conflicting-answers")}</h4>
                <p>{conflictDetail}</p>
              </div>
              <div className="document-answer-panel__resolve-badge document-answer-panel__resolve-badge--conflict">{conflictCount}</div>
            </div>

            {/* Bulunamayan Maddeler */}
            <div className="document-answer-panel__resolve-card">
              <div className="document-answer-panel__resolve-icon document-answer-panel__resolve-icon--missing">
                <Search width={20} height={20} />
              </div>
              <div className="document-answer-panel__resolve-card-text">
                <h4>{t("not-found-items-title")}</h4>
                <p>{notFoundDetail}</p>
              </div>
              <div className="document-answer-panel__resolve-badge">{notFoundCount}</div>
            </div>
          </div>
        </div>

        {/* Footer - Yeni Döküman */}
        <div className="document-answer-panel__footer">
          <button
            className="document-answer-panel__upload-btn"
            onClick={() => {
              setCompletionResult(null);
              setUploadingFile(null);
              setPanelView("upload");
            }}
          >
            <Upload width={18} height={18} />
            {t("new-document")}
          </button>
        </div>
      </>
    );
  };

  return (
    <div className={`document-answer-panel ${isOpen ? "open" : ""}`}>
      {renderHeader()}
      {panelView === "upload" && renderUploadView()}
      {panelView === "progress" && renderProgressView()}
      {panelView === "completed" && renderCompletedView()}
      {panelView === "previous-analyses" && renderPreviousAnalysesView()}
    </div>
  );
}
